import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { updateProfile } from '../api/auth';
import { useAuth } from '../context/AuthContext';

export function ProfilePage() {
  const { user, setUser } = useAuth();
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      setProfileMessage(data.message);
      setProfileError(null);
      if (data.user) {
        setUser(data.user);
      }
    },
    onError: (error: unknown) => {
      setProfileError((error as Error).message);
      setProfileMessage(null);
    },
  });

  const passwordMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      setPasswordMessage(data.message);
      setPasswordError(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error: unknown) => {
      setPasswordError((error as Error).message);
      setPasswordMessage(null);
    },
  });

  const handleProfileSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!fullName.trim()) {
      setProfileError('Full name is required');
      setProfileMessage(null);
      return;
    }
    profileMutation.mutate({ fullName: fullName.trim() });
  };

  const handlePasswordSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      setPasswordMessage(null);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      setPasswordMessage(null);
      return;
    }
    passwordMutation.mutate({
      password: currentPassword,
      newPassword,
    });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Profile</h1>
          <p>Update your personal details and password.</p>
        </div>
      </div>

      <div className="card">
        <h3>Personal details</h3>
        <form className="form" onSubmit={handleProfileSubmit}>
          <label>
            Email
            <input value={user?.email ?? ''} disabled />
          </label>
          <label>
            Full name
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          {profileError && <p className="form-error">{profileError}</p>}
          {profileMessage && <p className="form-success">{profileMessage}</p>}
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={profileMutation.isPending}>
              {profileMutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <h3>Change password</h3>
        <form className="form" onSubmit={handlePasswordSubmit}>
          <label>
            Current password
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
          </label>
          <div className="form-grid">
            <label>
              New password
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
            </label>
            <label>
              Confirm new password
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
            </label>
          </div>
          {passwordError && <p className="form-error">{passwordError}</p>}
          {passwordMessage && <p className="form-success">{passwordMessage}</p>}
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={passwordMutation.isPending}>
              {passwordMutation.isPending ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
